// How the Productivity Chart writes down a date, a time, a figure and the gap between two
// figures - and the instants it reads out of the payload in the first place. The vocabulary the rest
// of the chart's modules format through, so the same value never appears twice on screen written two
// ways. Free text typed on a farm is written down from here too, since the tooltips carrying it are
// rendered as markup.

import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import { PRODUCTIVITY_METRICS, type MetricKey } from './productivityMetrics';

// The tooltips these builders feed are rendered with `useHTML`, which hands their content to the
// browser as markup - the one path out of this component that Vue's own escaping does not cover.
// Everything on that path is free text somebody typed on a farm: a Note's wording, an Alarm's
// Cause, a Feed Mix's name and the remarks written against a feed change.
export function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// The one statement of how long these units are, for every module on this chart: the axis' tick
// strides, the series' column widths, the calendar rules drawn behind them and the readout's own
// week span all measure a day against this. Three separate declarations of it is how two of them
// come to disagree.
export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;
export const WEEK_MS = 7 * DAY_MS;

// A bucket's x-axis instant. The backend returns date and hour as separate fields (a day bucket
// has no time component at all); charting needs one combined instant. Parsed as UTC (an
// explicit "Z" for the hour form, since a bare "date" string is already UTC per spec) to match
// the UTC-based date handling used by the sibling Water/Mortality detail modals and
// WeightDetailModal's own bucketTimestamp - otherwise switching granularity would reinterpret the
// same reading in two different timezones for non-UTC users.
export function bucketTimestamp(bucket: { date: string; hour: string | null }): number {
    return productivityInstant(bucket.hour ? `${bucket.date}T${bucket.hour}` : bucket.date);
}

// How long one bucket's own period runs, per granularity. The one declaration of it: the series
// modules size their columns against it and the chart asks whether the farm's present still falls
// inside the last bucket, and two declarations is how those two come to disagree.
export const BUCKET_PERIOD_MS: Record<ProductivityGranularity, number> = {
    hour: HOUR_MS,
    day: DAY_MS,
};

export function bucketPeriodMs(granularity: ProductivityGranularity): number {
    return BUCKET_PERIOD_MS[granularity];
}

/**
 * Any date or time out of a Productivity Chart payload, as an axis instant.
 *
 * Every one of them - bucket dates and hours, Production Group spans, feed mix edges, note and alarm
 * timestamps, `directory_local_now` - is stated on the Directory's own wall clock with no offset
 * attached (see the `Productivity` type). Stamping them all as UTC puts them on one scale
 * that reads back unshifted under `time.useUTC`, whatever zone the viewer is in.
 *
 * This is the only way any of them may be parsed. Parsed as absolute instants instead, the markers
 * land on a scale offset from the buckets by the farm's own UTC offset: a note recorded at 02:00 on a
 * Nepalese farm pins to the previous day's gridline, and a note at 21:00 on an American one to the
 * next day's.
 */
export function productivityInstant(iso: string): number {
    // A bare date is already read as UTC by the parser; anything with a time needs saying.
    return Date.parse(iso.includes('T') ? `${iso}Z` : iso);
}

// What stands where a figure would be when there is none. One character rather than an empty cell,
// so a column of figures keeps its shape and "no reading" is visibly a reading that is missing.
export const PRODUCTIVITY_VALUE_PLACEHOLDER = '\u2014';

/**
 * One metric's figure as text, at the precision that metric is measured to (see the registry's
 * `decimals`). The single place a Productivity Chart figure becomes a string - the readout row,
 * its reference line and the on-chart cursor labels all read the same number and had all been
 * formatting it separately, so the same value could appear twice on screen written two ways.
 *
 * Rounded before formatting rather than formatted with a fixed fraction width: it is the idiom the
 * rest of these charts already use, and the locale's own `decimal` format is what decides the
 * separator.
 *
 * Guards on "is a number" rather than "is not null", because `n()` throws on anything else and one
 * missing field would otherwise take the whole chart down with an uncaught error instead of showing
 * a dash in one label.
 */
export function productivityMetricValue(key: MetricKey, value: number | null | undefined, granularity: ProductivityGranularity): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return PRODUCTIVITY_VALUE_PLACEHOLDER;
    const factor = 10 ** PRODUCTIVITY_METRICS[key].decimals(granularity);
    return i18n.global.n(Math.round(value * factor) / factor, 'decimal') as string;
}

/**
 * The two ways this chart ever writes a date or a time, and the whole of its date vocabulary: a
 * named-month date, and a bare time of day for the hour a bucket falls in. Deliberately not the
 * app's `short` or `long` formats, which state the month as a number - mixed in beside `middle`
 * they put "27.08.2026" on the same axis as "05. aug. 2026" and read as two different
 * applications. Where a date and a time are both wanted they are written as two of these, not as
 * a third format.
 */
export type ProductivityDateFormat = 'middle' | 'clock';

/**
 * The signed distance from `reference` to `actual`, in the metric's own unit and at the metric's own
 * precision - a gap written finer than either figure it came from would imply a precision neither has.
 * Null unless both sides have a figure: a gap against nothing is not a gap, and an unsigned one would
 * not say which side is ahead.
 *
 * In the unit rather than as a percentage: more is good for weight and bad for the dead count, so the
 * figure states the direction and leaves the judgement to the reader.
 *
 * Bracketed, and with a typographic minus rather than a hyphen. It is always written directly after
 * the figure it is measured from, and "3 -2" between two whole numbers reads as a subtraction rather
 * than as a value and its delta - which is exactly how it was misread. The sign is composed here
 * rather than left to the locale so that both directions are marked: an unsigned positive looks like
 * a second measurement.
 */
export function productivityMetricGap(key: MetricKey, actual: number | null, reference: number | null, granularity: ProductivityGranularity): string | null {
    if (actual === null || reference === null) return null;
    const gap = actual - reference;
    // Rounded to the metric's own precision before its sign is read, so a difference too small for
    // that precision to express is written as the nothing it is rather than as a signed zero.
    const magnitude = productivityMetricValue(key, Math.abs(gap), granularity);
    if (Number(magnitude.replace(/[^0-9]/g, '')) === 0) return `(\u00b1${magnitude})`;
    return `(${gap > 0 ? '+' : '\u2212'}${magnitude})`;
}

// The one way this chart renders a *bucket* instant as text - axis labels, the readout row and the
// cursor labels alike. Buckets are cut in UTC (see bucketTimestamp), so the formatter has to be told
// to read them back in UTC: left to the viewer's browser timezone, an hourly label is shifted by
// that offset, and west of UTC a midnight-anchored day bucket is named as the previous
// calendar day. The sibling Active groups charts force the same timezone for the same reason
// (activeGroupsChartFormatting.ts).
//
// Every instant on this chart, without exception - axis labels, the readout row, the cursor labels,
// and the Note/Alarm/Feed Mix tooltips alike. One formatter rather than one per kind: a Note may
// read as something that "genuinely happened at a point in time" and so belongs in the reader's own
// zone, but the server states its timestamp on the farm's clock like everything else, so naming it
// in the viewer's zone prints a different day from the gridline the same value is pinned to.
export function productivityDate(timestamp: number, key: ProductivityDateFormat): string {
    return i18n.global.d(timestamp, { key, timeZone: 'UTC' }) as string;
}
